// ============================================================================
// The Employees directory rules — who appears, in which group, and what the
// row says about them.
//
// WHY THIS FILE EXISTS. Until now the directory was two screens whose
// membership was inferred from an EMAIL DOMAIN:
//
//   /hr/people/full    employment_type='W2' AND email_work ILIKE '%terminalcleaning.com%'
//   /hr/people/hourly  employment_type='W2' AND NOT (that)
//
// Two things were wrong with that, and the second one is a bug.
//
//   1. The email domain was a PROXY for salaried-vs-hourly. `employees.pay_type`
//      now stores that as a fact (TCSD-0117), so the proxy is not needed and is
//      worse than the column: Wes Dittmer is a Chief Finance Officer on an
//      external address, and the proxy calls him hourly.
//   2. ⛔ BOTH queries required `employment_type = 'W2'`, so they were only
//      complementary on the EMAIL test. Anybody whose employment_type was
//      something else appeared in NEITHER list. The two `Subcontractor`
//      employees were already invisible, and now that `1099` is a valid
//      employment_type a 1099 worker would have been invisible too — which is
//      exactly what Antonela asked to fix when she said 1099s belong in the
//      directory under Hourly Paid.
//
// So: ONE screen, grouped by `pay_type`, and NOBODY IS FILTERED OUT BY CLASS.
// The worker class travels with the row as a chip instead of deciding whether
// the row exists.
//
// ⛔ THE BENEFITS NOTE ON A GROUP IS A LABEL, NOT A RULE. "Hourly paid /
// Not benefits-eligible" is what Antonela's mockup says and it is rendered
// here, but eligibility is stored per employee per plan in employee_benefits
// and `pay_type` is a separate fact from it on purpose (dispatch §3.4). Nothing
// in this file may be read as authorising a benefit or refusing one.
// ============================================================================

/** The pay-type buckets the directory renders, in display order. */
export type PayGroupKey = 'salaried' | 'hourly' | 'unset';

/** The fields the directory needs off an employees row. */
export interface DirectoryEmployee {
  employee_id: string;
  employee_name_first: string | null;
  employee_name_last: string | null;
  employee_title: string | null;
  pay_type: string | null;
  employment_type: string | null;
  employment_status: string | null;
  primary_facility_id: string | null;
  facility_name?: string | null;
  work_state?: string | null;
  email_work?: string | null;
}

/**
 * Statuses that mean "off the default roster".
 *
 * Kept identical to EMPLOYEE_INACTIVE_STATUSES in ./hooks/use-hr-data so the
 * directory and the archive toggle cannot disagree about who is current. If you
 * add a status in one place, the test in ./people-directory.test.ts fails.
 */
// ⛔ 'Former' WAS MISSING FROM THIS LIST until 2026-08-26, so the four people
// carrying it were shown on the DEFAULT ROSTER — current staff, in the
// directory Antonela works from. It only looked right because everybody who
// left recently was archived instead, and 'Archived' was here. Retiring
// 'Archived' would have made all seven former employees appear as current.
// 'Archived', 'Terminated' and 'Separated' are kept so reads stay tolerant for
// one release; nothing writes them any more.
export const OFF_ROSTER_STATUSES: ReadonlyArray<string> = [
  'Terminated',
  'Separated',
  'Archived',
  'Former',
];

export function isOffRoster(e: Pick<DirectoryEmployee, 'employment_status'>): boolean {
  return OFF_ROSTER_STATUSES.includes(e.employment_status ?? '');
}

/**
 * Which group a person belongs to.
 *
 * ⛔ Reads `pay_type` and NOTHING ELSE. Not the email domain, not
 * `employee_time`, not the job title. An unset pay type gets its own group
 * rather than being guessed into one of the other two: six employees genuinely
 * have no pay type yet, and filing them under "Hourly paid" would be the app
 * asserting something nobody has decided — and would quietly tell Antonela
 * they are not benefits-eligible.
 */
export function payGroupOf(e: Pick<DirectoryEmployee, 'pay_type'>): PayGroupKey {
  const pay = (e.pay_type ?? '').trim();
  if (pay === 'Salaried') return 'salaried';
  if (pay === 'Hourly') return 'hourly';
  return 'unset';
}

export interface GroupNote {
  label: string;
  /** 'ok' teal · 'warn' amber · 'muted' grey. */
  tone: 'ok' | 'warn' | 'muted';
}

/**
 * The note on a group header.
 *
 * The tone rule is the mockup's, restated so it is not a coincidence of
 * wording: a note that begins "Not" reads as a restriction and goes amber.
 * The unset group is deliberately neither — it is a to-do, not a verdict.
 */
export function benefitsNoteFor(key: PayGroupKey): GroupNote {
  if (key === 'salaried') return { label: 'Benefits-eligible', tone: 'ok' };
  if (key === 'hourly') return { label: 'Not benefits-eligible', tone: 'warn' };
  return { label: 'Pay type not set yet', tone: 'muted' };
}

export const GROUP_TITLES: Record<PayGroupKey, string> = {
  salaried: 'Salaried',
  hourly: 'Hourly paid',
  unset: 'Pay type not set',
};

/**
 * The worker-class chip, or null when there is nothing worth saying.
 *
 * `W2` is the unremarkable case and gets no chip — chipping every row would
 * make the one that matters invisible. `1099` and `Subcontractor` DO get one,
 * because Antonela asked for 1099s to sit inside Hourly Paid and a 1099 is not
 * a W2 hourly employee: different tax treatment, different documents, and no
 * COI requirement. Grouping them together is right; conflating them is not.
 */
export function workerClassChip(
  e: Pick<DirectoryEmployee, 'employment_type'>,
): string | null {
  const t = (e.employment_type ?? '').trim();
  if (t === '' || t === 'W2') return null;
  return t;
}

/** One current compensation row, as v_employee_compensation_current returns it. */
export interface CurrentComp {
  employee_id: string;
  wage_rate: number | null;
  pay_frequency: string | null;
  standard_hours: number | null;
}

/**
 * The Pay rate cell.
 *
 * ⛔ `wage_rate` is a NUMBER and is formatted from the number — never read as a
 * flag and never concatenated from a stored display string. The mockup shows
 * the literal word "Salary" for salaried people because it carries no figure;
 * that is a display choice for a MISSING rate, so a salaried employee who does
 * have a rate on file shows it rather than being hidden behind the word.
 */
export function formatPayRate(
  comp: CurrentComp | null | undefined,
  payType: string | null | undefined,
): string {
  const rate = comp?.wage_rate;
  const hasRate = typeof rate === 'number' && Number.isFinite(rate) && rate > 0;
  const pay = (payType ?? '').trim();

  if (!hasRate) return pay === 'Salaried' ? 'Salary' : '—';

  const money = `$${(rate as number).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return pay === 'Hourly' ? `${money} / hr` : money;
}

/** Standard hours, as the card and the row show it. */
export function formatStandardHours(comp: CurrentComp | null | undefined): string {
  const h = comp?.standard_hours;
  if (typeof h !== 'number' || !Number.isFinite(h) || h <= 0) return '—';
  // Trim a trailing .00 — "40 / week" reads better than "40.00 / week".
  const n = Number.isInteger(h) ? String(h) : String(h);
  return `${n} / week`;
}

export function displayName(
  e: Pick<DirectoryEmployee, 'employee_name_first' | 'employee_name_last' | 'employee_id'>,
): string {
  const name = [e.employee_name_first, e.employee_name_last]
    .filter((s) => !!s && s.trim() !== '')
    .join(' ')
    .trim();
  return name === '' ? e.employee_id : name;
}

export interface DirectoryGroup {
  key: PayGroupKey;
  title: string;
  note: GroupNote;
  rows: DirectoryEmployee[];
}

/**
 * Build the groups the directory renders.
 *
 * Always returns the salaried and hourly groups, EVEN WHEN EMPTY — an absent
 * group reads as "no such category" rather than "nobody in it yet", and the
 * mockup renders a zero count rather than hiding the header. The unset group is
 * the exception: it appears only when somebody is in it, because it is a
 * worklist and an empty worklist is noise.
 *
 * ⛔ No class filter. Every row handed in is placed somewhere.
 */
export function buildDirectoryGroups(
  rows: DirectoryEmployee[],
  opts?: { offRoster?: boolean },
): DirectoryGroup[] {
  const wantOffRoster = opts?.offRoster ?? false;
  const inScope = rows.filter((e) => isOffRoster(e) === wantOffRoster);

  const byKey: Record<PayGroupKey, DirectoryEmployee[]> = {
    salaried: [],
    hourly: [],
    unset: [],
  };
  for (const e of inScope) byKey[payGroupOf(e)].push(e);

  const order: PayGroupKey[] = ['salaried', 'hourly', 'unset'];
  return order
    .filter((k) => k !== 'unset' || byKey[k].length > 0)
    .map((k) => ({
      key: k,
      title: GROUP_TITLES[k],
      note: benefitsNoteFor(k),
      rows: byKey[k].sort((a, b) =>
        displayName(a).localeCompare(displayName(b), 'en-US'),
      ),
    }));
}

/**
 * How many people the directory can say nothing definite about.
 *
 * Surfaced as a number on the page rather than left for somebody to notice,
 * because it is Antonela's to-do list and TCSD-0080's rule is that work gets
 * reported with a location and an instruction, not as a count in a footnote.
 */
export function unsetPayTypeCount(rows: DirectoryEmployee[]): number {
  return rows.filter((e) => !isOffRoster(e) && payGroupOf(e) === 'unset').length;
}
